import _ from 'lodash';
import { useMemo } from 'react';
import { t } from '@lingui/macro';
import { getDefaultMetricColor, usePaletteType } from '../../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../../utilities/FormattingUtilities';

const trendDefault = {
    formatter: FORMATTER_TYPES.LOG,
    units: '',
    label: '',
    chartType: 'spline',
    key: undefined,
    color: undefined,
    multiplier: 1,
};

/**
 * Build a set of chartTrend objects from the metricPatternMapping patterns and the keys contained in the
 * first item of the trend data. metricPatternMapping can contain the following properties:
 *
 *     pattern      - The "starts with" pattern for the trend data keys.
 *     trendOptions - The optional set of overrides to use for trendDefaults on this trend.
 *     noI18n       - Do not use i18n to attempt to translate the trend label.
 *
 * @param {Object} metricPatternMapping
 * @param {Object} data
 * @returns {[Object]}
 */
export function useMetricPattern(metricPatternMapping, data) {
    const trends = data?.trends;
    const paletteType = usePaletteType();
    return useMemo(() => {
        if (!metricPatternMapping || metricPatternMapping.length === 0 || !trends) {
            return [];
        }
        const keys = _.keys(trends);
        return _(metricPatternMapping)
            .map((mp) => {
                return _(keys)
                    .filter((k) => k.startsWith(mp.pattern))
                    .map((key, index) => {
                        const name = key.substr(mp.pattern.length);
                        const color = getDefaultMetricColor(index, paletteType);
                        let label = mp.noI18n ? name : t`${name}`;
                        if (mp.capitalize) {
                            label = label.toUpperCase();
                        }
                        return _.merge(_.cloneDeep(trendDefault), mp.trendOptions || {}, {
                            key,
                            label,
                            color,
                        });
                    })
                    .value();
            })
            .flatten()
            .value();
    }, [metricPatternMapping, paletteType, trends]);
}
